import { useEffect, useState } from 'react'
import { Loader2, Flag, X, ChevronDown } from 'lucide-react'
import { adminListReports, adminActOnReport } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'

const STATUS_TABS = ['pending', 'actioned', 'dismissed']

const CATEGORY_STYLES = {
  spam: 'bg-yellow-100 text-yellow-800',
  hate: 'bg-red-100 text-red-800',
  sexual: 'bg-pink-100 text-pink-800',
  violence: 'bg-orange-100 text-orange-800',
  misinformation: 'bg-purple-100 text-purple-800',
  other: 'bg-gray-100 text-gray-700',
}

const TYPE_STYLES = {
  announcement: 'bg-blue-50 text-blue-700',
  event: 'bg-emerald-50 text-emerald-700',
  parish_profile: 'bg-gold/10 text-charcoal',
  user_display_name: 'bg-gray-100 text-gray-700',
}

function Badge({ value, styleMap, fallback = 'bg-gray-100 text-gray-700' }) {
  const cls = styleMap[value] || fallback
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium capitalize ${cls}`}>
      {value?.replace(/_/g, ' ') || '—'}
    </span>
  )
}

export default function Reports() {
  const [items, setItems] = useState([])
  const [status, setStatus] = useState('pending')
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [note, setNote] = useState('')
  const [acting, setActing] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try {
      const data = await adminListReports({ status })
      setItems(data || [])
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [status])

  const openReport = (r) => {
    setSelected(r)
    setNote('')
  }

  const act = async (action) => {
    setActing(true)
    try {
      await adminActOnReport(selected.id, { action, note: note || undefined })
      setSelected(null)
      refresh()
    } finally {
      setActing(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-display text-charcoal mb-1">Content Reports</h1>
          <p className="text-charcoal-muted text-sm">
            {loading ? 'Loading…' : `${items.length} report${items.length !== 1 ? 's' : ''} · ${status}`}
          </p>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 mb-4 bg-white border border-surface-border rounded-lg p-1 w-fit">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-4 py-1.5 rounded text-sm font-medium capitalize transition-colors ${
              status === s
                ? 'bg-gold text-black'
                : 'text-charcoal-muted hover:text-charcoal'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-linen/50 border-b border-surface-border">
                <tr>
                  {['Date', 'Reporter', 'Type', 'Category', 'Parish', ''].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-charcoal-muted uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Flag className="w-7 h-7 text-charcoal-subtle mx-auto mb-2" />
                      <p className="text-charcoal-muted text-sm">No {status} reports.</p>
                    </td>
                  </tr>
                ) : (
                  items.map((r) => (
                    <tr key={r.id} className="hover:bg-surface-linen/30 transition-colors">
                      <td className="px-4 py-3 text-sm text-charcoal whitespace-nowrap">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-charcoal/90">
                        {r.reporter?.name || r.reporter?.email || <span className="text-charcoal-subtle italic">Unknown</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={r.contentType} styleMap={TYPE_STYLES} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={r.category} styleMap={CATEGORY_STYLES} />
                      </td>
                      <td className="px-4 py-3 text-sm text-charcoal/80">
                        {r.parish?.name || <span className="text-charcoal-subtle">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openReport(r)}
                          className="text-charcoal-muted hover:text-charcoal h-8 text-xs"
                        >
                          Review
                          <ChevronDown className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-xl overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border">
              <h2 className="font-semibold text-charcoal font-display">Review Report</h2>
              <button
                onClick={() => setSelected(null)}
                className="text-charcoal-subtle hover:text-charcoal transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Report details */}
            <div className="px-6 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-charcoal-muted uppercase tracking-wide mb-0.5">Type</p>
                  <Badge value={selected.contentType} styleMap={TYPE_STYLES} />
                </div>
                <div>
                  <p className="text-xs text-charcoal-muted uppercase tracking-wide mb-0.5">Category</p>
                  <Badge value={selected.category} styleMap={CATEGORY_STYLES} />
                </div>
                <div>
                  <p className="text-xs text-charcoal-muted uppercase tracking-wide mb-0.5">Reporter</p>
                  <p className="text-charcoal">{selected.reporter?.name || selected.reporter?.email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-charcoal-muted uppercase tracking-wide mb-0.5">Parish</p>
                  <p className="text-charcoal">{selected.parish?.name || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-charcoal-muted uppercase tracking-wide mb-0.5">Date</p>
                  <p className="text-charcoal">{new Date(selected.createdAt).toLocaleString()}</p>
                </div>
                {selected.notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-charcoal-muted uppercase tracking-wide mb-0.5">Notes</p>
                    <p className="text-charcoal bg-surface-linen rounded p-2 text-sm">{selected.notes}</p>
                  </div>
                )}
              </div>

              {/* Resolution note */}
              <div>
                <Label htmlFor="resolution-note" className="text-xs font-medium text-charcoal-muted uppercase tracking-wide mb-1.5 block">
                  Resolution note (optional)
                </Label>
                <textarea
                  id="resolution-note"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note…"
                  className="w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal placeholder:text-charcoal-subtle focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-white resize-none"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 px-6 py-4 border-t border-surface-border bg-surface-linen/30">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => act('dismiss')}
                disabled={acting}
                className="text-charcoal-muted hover:text-charcoal text-xs"
              >
                {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Dismiss'}
              </Button>
              <Button
                size="sm"
                onClick={() => act('remove_content')}
                disabled={acting}
                className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs"
              >
                Remove content
              </Button>
              <Button
                size="sm"
                onClick={() => act('ban_parish')}
                disabled={acting}
                className="bg-red-600 hover:bg-red-700 text-white text-xs"
              >
                Ban parish
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
